/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { devisAPI } from '../services/api';

const TVA_OPTIONS = [0, 5.5, 10, 20];
const UNITES = ['m²', 'm³', 'ml', 'u', 'h', 'forfait', 'jour', 'kg', 't'];

function Input({ label, value, onChange, placeholder, type = 'text', required }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#4a7abf', marginBottom: 4, fontWeight: 500 }}>
        {label}{required && ' *'}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', background: '#111', border: '0.5px solid #1a2744', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#c8d4e8', outline: 'none', boxSizing: 'border-box' }}
      />
    </div>
  );
}

function PopupConfirmation({ email, nom, montant, type, onConfirm, onCancel, loading }) {
  const typeLabel = type === 'devis' ? 'devis' : 'facture';
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#0d1117', border: '0.5px solid #1a2744', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380 }}>
        <p style={{ color: '#e8eaf0', fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>Confirmer l'envoi</p>
        <p style={{ color: '#4a7abf', fontSize: 13, margin: '0 0 20px', lineHeight: 1.6 }}>
          Le {typeLabel} de <strong style={{ color: '#c8d4e8' }}>{montant?.toFixed(2)} € TTC</strong> sera envoyé à :
        </p>
        <div style={{ background: '#111', border: '0.5px solid #1a2744', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
          <p style={{ color: '#FFD700', fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>{nom}</p>
          <p style={{ color: '#4a7abf', fontSize: 12, margin: 0 }}>{email}</p>
        </div>
        <p style={{ color: '#666', fontSize: 11, margin: '0 0 20px', lineHeight: 1.5 }}>
          Le client pourra répondre directement depuis son email. Vérifiez que l'email est correct avant de confirmer.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} disabled={loading} style={{ flex: 1, background: 'none', border: '0.5px solid #1a2744', borderRadius: 20, padding: '11px 0', fontSize: 13, color: '#4a7abf', cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, background: loading ? '#1a2744' : '#FFD700', border: 'none', borderRadius: 20, padding: '11px 0', fontSize: 13, fontWeight: 700, color: loading ? '#4a7abf' : '#0a0a0a', cursor: loading ? 'default' : 'pointer' }}>
            {loading ? 'Envoi...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Devis({ artisan, devisPreRempli, onDevisGenere, onSwitchTab }) {
  const [tab, setTab] = useState('nouveau');
  const [type, setType] = useState('devis');
  const [loading, setLoading] = useState(false);
  const [loadingEnvoi, setLoadingEnvoi] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [envoyeSuccess, setEnvoyeSuccess] = useState(false);

  const [form, setForm] = useState({
    client_nom: '', client_adresse: '', client_telephone: '', client_email: '',
    description_travaux: '', date_debut: '', date_fin: '', notes: '',
    lignes: [{ description: '', quantite: 1, unite: 'm²', prix_unitaire: 0, tva: 10 }]
  });

  useEffect(() => {
    if (devisPreRempli) {
      setType(devisPreRempli.type_document === 'facture' ? 'facture' : 'devis');
      setForm({
        client_nom: devisPreRempli.client_nom || '',
        client_adresse: devisPreRempli.client_adresse || '',
        client_telephone: devisPreRempli.client_telephone || '',
        client_email: devisPreRempli.client_email || '',
        description_travaux: devisPreRempli.description_travaux || '',
        date_debut: devisPreRempli.date_debut || '',
        date_fin: devisPreRempli.date_fin || '',
        notes: devisPreRempli.notes || '',
        lignes: devisPreRempli.lignes && devisPreRempli.lignes.length > 0
          ? devisPreRempli.lignes.map(l => ({
            description: l.description || '',
            quantite: parseFloat(l.quantite) || 1,
            unite: l.unite || 'm²',
            prix_unitaire: parseFloat(l.prix_unitaire) || 0,
            tva: parseFloat(l.tva) || 10,
          }))
          : [{ description: '', quantite: 1, unite: 'm²', prix_unitaire: 0, tva: 10 }]
      });
      setTab('nouveau');
      setPdfUrl(null);
      setPdfData(null);
      setEnvoyeSuccess(false);
    }
  }, [devisPreRempli]);

  useEffect(() => {
    if (artisan?.id) chargerDocuments();
  }, [artisan]); // eslint-disable-line react-hooks/exhaustive-deps

  const chargerDocuments = async () => {
    try {
      const docs = await devisAPI.getListe(artisan.id);
      setDocuments(docs);
    } catch {}
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const setLigne = (i, key, val) => setForm(f => {
    const lignes = [...f.lignes];
    lignes[i] = { ...lignes[i], [key]: val };
    return { ...f, lignes };
  });

  const addLigne = () => setForm(f => ({
    ...f, lignes: [...f.lignes, { description: '', quantite: 1, unite: 'm²', prix_unitaire: 0, tva: 10 }]
  }));

  const removeLigne = (i) => setForm(f => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));

  const totalHT = form.lignes.reduce((s, l) => s + (l.quantite * l.prix_unitaire), 0);
  const totalTTC = form.lignes.reduce((s, l) => s + (l.quantite * l.prix_unitaire * (1 + l.tva / 100)), 0);

  const generer = async () => {
    if (!form.client_nom) return alert('Le nom du client est obligatoire.');
    if (!form.client_email) return alert("L'email du client est obligatoire pour pouvoir envoyer le document.");
    if (!form.description_travaux) return alert('La description des travaux est obligatoire.');
    setLoading(true);
    setPdfUrl(null);
    setPdfData(null);
    setEnvoyeSuccess(false);
    try {
      const result = await devisAPI.generer({
        type,
        artisan_id: artisan?.id,
        artisan_nom: artisan?.nom_entreprise || 'Mon Entreprise',
        artisan_siret: artisan?.siret || '000 000 000 00000',
        artisan_adresse: artisan?.adresse || '',
        artisan_telephone: artisan?.telephone || '',
        artisan_email: artisan?.email || '',
        artisan_tva: artisan?.tva_numero,
        artisan_assurance: artisan?.assurance_decennale,
        ...form,
      });
      setPdfUrl(result.url);
      setPdfData({
        documentId: result.documentId,
        numero: result.numero,
        montantTTC: result.montantTTC,
      });
      window.open(result.url, '_blank');
      if (onDevisGenere) onDevisGenere();
    } catch (e) {
      alert('Erreur lors de la génération : ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const envoyer = async () => {
    setLoadingEnvoi(true);
    try {
      await devisAPI.envoyer({
        type,
        artisan_id: artisan?.id,
        artisan_nom: artisan?.nom_entreprise || 'Mon Entreprise',
        artisan_siret: artisan?.siret || '000 000 000 00000',
        artisan_adresse: artisan?.adresse || '',
        artisan_telephone: artisan?.telephone || '',
        artisan_email: artisan?.email || '',
        artisan_tva: artisan?.tva_numero,
        artisan_assurance: artisan?.assurance_decennale,
        ...form,
        document_id: pdfData?.documentId || null,
      });
      setShowConfirmation(false);
      setEnvoyeSuccess(true);
      await chargerDocuments();
      setTimeout(() => {
        setTab('liste');
        setEnvoyeSuccess(false);
        setPdfUrl(null);
        setPdfData(null);
      }, 2000);
    } catch (e) {
      alert('Erreur lors de l\'envoi : ' + e.message);
    } finally {
      setLoadingEnvoi(false);
    }
  };

  const envoyerDepuisListe = async (doc) => {
    setLoadingEnvoi(true);
    try {
      await devisAPI.envoyer({
        type: doc.type,
        artisan_id: artisan?.id,
        artisan_nom: artisan?.nom_entreprise || 'Mon Entreprise',
        artisan_siret: artisan?.siret || '000 000 000 00000',
        artisan_adresse: artisan?.adresse || '',
        artisan_telephone: artisan?.telephone || '',
        artisan_email: artisan?.email || '',
        artisan_tva: artisan?.tva_numero,
        artisan_assurance: artisan?.assurance_decennale,
        client_nom: doc.client_nom,
        client_adresse: doc.client_adresse || '',
        client_telephone: doc.client_telephone || '',
        client_email: doc.client_email || '',
        description_travaux: doc.description_travaux || '',
        notes: '',
        lignes: doc.lignes || [{ description: doc.description_travaux, quantite: 1, unite: 'forfait', prix_unitaire: doc.montant_ht || 0, tva: 10 }],
        document_id: doc.id,
      });
      await chargerDocuments();
      alert('Email envoyé avec succès !');
    } catch (e) {
      alert("Erreur lors de l'envoi : " + e.message);
    } finally {
      setLoadingEnvoi(false);
    }
  };

  const statusColor = {
    brouillon: '#4a7abf',
    envoye: '#f0a500',
    signe: '#4caf7d',
    paye: '#4caf7d',
    annule: '#e05a5a',
    impaye: '#e05a5a',
  };

  const statusLabel = {
    brouillon: 'Brouillon',
    envoye: 'Envoyé ✉️',
    signe: 'Signé',
    paye: 'Payé',
    annule: 'Annulé',
    impaye: 'Impayé',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0a0a0a' }}>

      {showConfirmation && (
        <PopupConfirmation
          email={form.client_email}
          nom={form.client_nom}
          montant={pdfData?.montantTTC || totalTTC}
          type={type}
          onConfirm={envoyer}
          onCancel={() => setShowConfirmation(false)}
          loading={loadingEnvoi}
        />
      )}

      <div style={{ background: '#0d1117', padding: '14px 16px 12px', borderBottom: '0.5px solid #1a2744', flexShrink: 0 }}>
        {devisPreRempli && (
          <div style={{ background: '#0e2a1a', border: '0.5px solid #2a4a2a', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: '#4caf7d' }}>
            Devis pré-rempli par l'IA — vérifiez et cliquez sur "Générer le PDF"
          </div>
        )}
        <p style={{ color: '#e8eaf0', fontSize: 15, fontWeight: 600, margin: '0 0 10px' }}>Devis & Factures</p>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'nouveau', label: '+ Nouveau' },
            { id: 'liste', label: 'Mes documents' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: tab === t.id ? '#FFD700' : '#111',
              color: tab === t.id ? '#0a0a0a' : '#4a7abf',
              border: `0.5px solid ${tab === t.id ? '#FFD700' : '#1a2744'}`,
              borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px' }}>

        {tab === 'nouveau' && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {['devis', 'facture'].map(t => (
                <button key={t} onClick={() => setType(t)} style={{
                  flex: 1,
                  background: type === t ? (t === 'devis' ? '#0e2040' : '#1a2a00') : '#111',
                  border: `0.5px solid ${type === t ? (t === 'devis' ? '#1e3a6e' : '#2a4a00') : '#1a2744'}`,
                  borderRadius: 8, padding: '10px 0', fontSize: 13, fontWeight: 600,
                  color: type === t ? (t === 'devis' ? '#85B7EB' : '#4caf7d') : '#4a7abf',
                  cursor: 'pointer',
                }}>
                  {t === 'devis' ? 'Devis' : 'Facture'}
                </button>
              ))}
            </div>

            <p style={{ fontSize: 12, color: '#FFD700', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Client</p>
            <Input label="Nom / Raison sociale" value={form.client_nom} onChange={v => setField('client_nom', v)} required placeholder="M. Dupont" />
            <Input label="Email client" value={form.client_email} onChange={v => setField('client_email', v)} required type="email" placeholder="client@email.fr" />
            <Input label="Téléphone" value={form.client_telephone} onChange={v => setField('client_telephone', v)} placeholder="06 00 00 00 00" />
            <Input label="Adresse" value={form.client_adresse} onChange={v => setField('client_adresse', v)} placeholder="12 rue des Fleurs, 75001 Paris" />

            <p style={{ fontSize: 12, color: '#FFD700', fontWeight: 600, margin: '16px 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Travaux</p>
            <Input label="Description des travaux" value={form.description_travaux} onChange={v => setField('description_travaux', v)} required placeholder="Réfection toiture — tuiles canal" />

            <p style={{ fontSize: 12, color: '#FFD700', fontWeight: 600, margin: '16px 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Lignes</p>
            {form.lignes.map((ligne, i) => (
              <div key={i} style={{ background: '#0d1117', border: '0.5px solid #1a2744', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                <Input label="Description" value={ligne.description} onChange={v => setLigne(i, 'description', v)} placeholder="Fourniture et pose" />
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <Input label="Qté" value={ligne.quantite} onChange={v => setLigne(i, 'quantite', parseFloat(v) || 0)} type="number" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: '#4a7abf', marginBottom: 4, fontWeight: 500 }}>Unité</label>
                    <select value={ligne.unite} onChange={e => setLigne(i, 'unite', e.target.value)}
                      style={{ width: '100%', background: '#111', border: '0.5px solid #1a2744', borderRadius: 8, padding: '9px 8px', fontSize: 13, color: '#c8d4e8', outline: 'none' }}>
                      {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input label="P.U. HT €" value={ligne.prix_unitaire} onChange={v => setLigne(i, 'prix_unitaire', parseFloat(v) || 0)} type="number" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 11, color: '#4a7abf', marginBottom: 4, fontWeight: 500 }}>TVA %</label>
                    <select value={ligne.tva} onChange={e => setLigne(i, 'tva', parseFloat(e.target.value))}
                      style={{ width: '100%', background: '#111', border: '0.5px solid #1a2744', borderRadius: 8, padding: '9px 8px', fontSize: 13, color: '#c8d4e8', outline: 'none' }}>
                      {TVA_OPTIONS.map(t => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </div>
                </div>
                {form.lignes.length > 1 && (
                  <button onClick={() => removeLigne(i)} style={{ background: 'none', border: 'none', color: '#e05a5a', fontSize: 11, cursor: 'pointer', marginTop: 4 }}>
                    Supprimer cette ligne
                  </button>
                )}
              </div>
            ))}
            <button onClick={addLigne} style={{ background: 'none', border: '0.5px dashed #1a2744', borderRadius: 8, padding: '8px 0', width: '100%', color: '#4a7abf', fontSize: 12, cursor: 'pointer', marginBottom: 16 }}>
              + Ajouter une ligne
            </button>

            <div style={{ background: '#0d1117', border: '0.5px solid #1a2744', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#4a7abf', marginBottom: 4 }}>
                <span>Total HT</span><span>{totalHT.toFixed(2)} €</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: '#FFD700', borderTop: '0.5px solid #1a2744', paddingTop: 8, marginTop: 4 }}>
                <span>Total TTC</span><span>{totalTTC.toFixed(2)} €</span>
              </div>
            </div>

            <Input label="Notes / Conditions" value={form.notes} onChange={v => setField('notes', v)} placeholder="Délai de paiement : 30 jours…" />

            {envoyeSuccess && (
              <div style={{ background: '#0e2a1a', border: '0.5px solid #4caf7d', borderRadius: 10, padding: '12px 14px', marginBottom: 12, textAlign: 'center' }}>
                <p style={{ color: '#4caf7d', fontSize: 13, fontWeight: 600, margin: 0 }}>Email envoyé avec succès !</p>
              </div>
            )}

            <button onClick={generer} disabled={loading} style={{
              width: '100%', background: loading ? '#1a2744' : '#FFD700',
              color: loading ? '#4a7abf' : '#0a0a0a',
              border: 'none', borderRadius: 20, padding: '13px 0', fontSize: 14,
              fontWeight: 700, cursor: loading ? 'default' : 'pointer', marginBottom: 10,
            }}>
              {loading ? 'Génération en cours…' : 'Générer le PDF'}
            </button>

            {pdfUrl && (
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{
                  flex: 1, display: 'block', textAlign: 'center',
                  background: '#0e2040', border: '0.5px solid #1e3a6e',
                  borderRadius: 20, padding: '11px 0', fontSize: 13,
                  color: '#85B7EB', textDecoration: 'none', fontWeight: 500,
                }}>
                  Voir le PDF
                </a>
                <button onClick={() => setShowConfirmation(true)} style={{
                  flex: 1, background: '#4caf7d', border: 'none',
                  borderRadius: 20, padding: '11px 0', fontSize: 13,
                  color: '#fff', cursor: 'pointer', fontWeight: 600,
                }}>
                  Envoyer par email
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'liste' && (
          <>
            {documents.length === 0 ? (
              <p style={{ color: '#4a7abf', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                Aucun document pour le moment.
              </p>
            ) : documents.map(doc => (
              <div key={doc.id} style={{
                background: '#0d1117',
                border: `0.5px solid ${doc.statut === 'impaye' ? '#e05a5a' : '#1a2744'}`,
                borderRadius: 10, padding: '12px 14px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <p style={{ color: '#c8d4e8', fontSize: 13, fontWeight: 500, margin: '0 0 3px' }}>{doc.client_nom}</p>
                    <p style={{ color: '#4a7abf', fontSize: 11, margin: 0 }}>{doc.numero}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ color: '#FFD700', fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>
                      {doc.montant_ttc?.toFixed(2)} €
                    </p>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 10,
                      background: '#0e2040', color: statusColor[doc.statut] || '#4a7abf',
                      fontWeight: 500,
                    }}>
                      {statusLabel[doc.statut] || doc.statut}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  {doc.statut === 'impaye' && (
                    <button onClick={async () => {
                      try {
                        await devisAPI.updateStatut(doc.id, 'paye');
                        await chargerDocuments();
                      } catch {}
                    }} style={{
                      flex: 1, background: '#0e2a1a', border: '0.5px solid #4caf7d',
                      borderRadius: 8, padding: '6px 0', fontSize: 11, color: '#4caf7d',
                      cursor: 'pointer', fontWeight: 500,
                    }}>
                      Marquer payé
                    </button>
                  )}
                  {(doc.statut === 'brouillon' || doc.statut === 'envoye') && doc.client_email && (
                    <button onClick={() => envoyerDepuisListe(doc)} disabled={loadingEnvoi} style={{
                      flex: 1, background: '#0e2040', border: '0.5px solid #1e3a6e',
                      borderRadius: 8, padding: '6px 0', fontSize: 11, color: '#85B7EB',
                      cursor: loadingEnvoi ? 'default' : 'pointer', fontWeight: 500,
                    }}>
                      {loadingEnvoi ? 'Envoi...' : 'Envoyer par email'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
